// ══════════════════════════════════════════════════════════
// usePlanPermissions — REAL billing entitlements from Supabase
//
// Fuente de verdad: subscriptions + plan_features en Supabase.
// CERO mock data. CERO flags locales. CERO hardcodeo de slugs.
//
// Expone:
//   NEW API (spec bloque 51):
//     loading, error, subscription, entitlements,
//     hasFeature(featureCode), getLimit(featureCode),
//     isSubscriptionActive, refreshEntitlements()
//
//   BACKWARD COMPAT (migración gradual):
//     currentPlan, planId, storageUsed, storageTotalGB,
//     storageUsedGB, storagePercentage, canAccess, getFeature,
//     isFree, isPro, isPremium
// ══════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useMemo } from 'react';
import { getSupabaseClient } from '@/services/supabase/client';
import type { EntitlementKey, EntitlementsMap } from '@/types/entitlements';
import { ENTITLEMENT_DEFINITIONS, buildEmptyEntitlementsMap } from '@/types/entitlements';

// ── CONSTANTS ──

const FEATURE_CODES: EntitlementKey[] = [
  'managed_streaming', 'autodj', 'extra_storage', 'multiple_stations',
  'advanced_cabina', 'podcasts', 'contests', 'analytics', 'custom_domain',
  'featured_radio', 'advanced_notifications', 'custom_app',
];

/**
 * Mapeo de viejos feature IDs mock → códigos reales de billing.
 * Los IDs legacy (fotos, videos, lives, etc.) ya no existen en el sistema
 * real. Se mapean al feature_code más cercano para mantener compatibilidad
 * con las páginas que aún no migraron a los 12 códigos estándar.
 */
const LEGACY_FEATURE_MAP: Record<string, EntitlementKey> = {
  fotos: 'extra_storage',
  videos: 'extra_storage',
  lives: 'extra_storage',
  custom_domain: 'custom_domain',
  galerias: 'extra_storage',
  upload_fotos: 'extra_storage',
  upload_videos: 'extra_storage',
  storage_extendido: 'extra_storage',
  storage: 'extra_storage',
  posts_con_imagenes: 'extra_storage',
  comunidad_completa: 'advanced_cabina',
  analytics_avanzado: 'analytics',
  frecuencia_premium: 'featured_radio',
  soporte_prioritario: 'advanced_notifications',
};

// ── TYPES ──

interface PlanInfoLegacy {
  id: string;
  name: string;
  badge: string;
  storageGB: number;
  color: string;
  price: string;
}

interface FeatureInfoLegacy {
  accessible: boolean;
  requiredPlan: PlanInfoLegacy;
  badge: string;
  badgeColor: string;
  description: string;
  label: string;
}

interface SubscriptionData {
  planId: string;
  planName: string;
  planSlug: string;
  planCode: string;
  planPrice: number;
  planCurrency: string;
  status: string;
  featureCodes: string[];
  storageGB: number;
  features: Record<string, string | number | boolean>;
}

export interface UsePlanPermissionsReturn {
  // ── NEW API (spec bloque 51) ──
  loading: boolean;
  error: string | null;
  subscription: SubscriptionData | null;
  entitlements: EntitlementsMap;
  hasFeature: (featureCode: string) => boolean;
  getLimit: (featureCode: string) => number | null;
  isSubscriptionActive: boolean;
  refreshEntitlements: () => void;

  // ── BACKWARD COMPAT (migración gradual) ──
  currentPlan: PlanInfoLegacy;
  planId: string;
  storageUsed: string;
  storageTotalGB: number;
  storageUsedGB: number;
  storagePercentage: number;
  canAccess: (featureId: string) => boolean;
  getFeature: (featureId: string) => FeatureInfoLegacy | null;
  isFree: boolean;
  isPro: boolean;
  isPremium: boolean;
}

// ── HELPERS ──

function supabase() {
  return getSupabaseClient();
}

function resolveFeatureCode(featureIdOrCode: string): EntitlementKey | null {
  // Si ya es un feature code estándar
  if (FEATURE_CODES.includes(featureIdOrCode as EntitlementKey)) {
    return featureIdOrCode as EntitlementKey;
  }
  // Mapear desde ID legacy
  return LEGACY_FEATURE_MAP[featureIdOrCode] ?? null;
}

function formatStorageUsed(storageGB: number): string {
  if (storageGB < 1) return `${(storageGB * 1000).toFixed(0)} MB`;
  return `${storageGB.toFixed(1)} GB`;
}

function buildPlanInfoLegacy(data: SubscriptionData | null): PlanInfoLegacy {
  if (!data) {
    return {
      id: 'free',
      name: 'Free',
      badge: 'Starter',
      storageGB: 1,
      color: 'secondary',
      price: 'Gratis',
    };
  }

  const hasManagedStreaming = data.featureCodes.includes('managed_streaming');
  const hasAnalytics = data.featureCodes.includes('analytics');
  const isHighTier = data.planPrice >= 80;

  if (isHighTier || (hasManagedStreaming && hasAnalytics)) {
    return {
      id: data.planSlug,
      name: data.planName,
      badge: 'Profesional',
      storageGB: data.storageGB,
      color: 'accent',
      price: `$${data.planPrice}/mes`,
    };
  }

  if (hasManagedStreaming || data.planPrice >= 20) {
    return {
      id: data.planSlug,
      name: data.planName,
      badge: 'Más popular',
      storageGB: data.storageGB,
      color: 'primary',
      price: `$${data.planPrice}/mes`,
    };
  }

  if (data.planPrice > 0) {
    return {
      id: data.planSlug,
      name: data.planName,
      badge: 'Starter',
      storageGB: data.storageGB,
      color: 'primary',
      price: `$${data.planPrice}/mes`,
    };
  }

  return {
    id: 'free',
    name: 'Free',
    badge: 'Starter',
    storageGB: 1,
    color: 'secondary',
    price: 'Gratis',
  };
}

// ── HOOK ──

export function usePlanPermissions(): UsePlanPermissionsReturn {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData | null>(null);
  const [entitlements, setEntitlements] = useState<EntitlementsMap>(buildEmptyEntitlementsMap());
  const [refreshKey, setRefreshKey] = useState(0);

  const refreshEntitlements = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        // 1. Obtener perfil del usuario
        const { data: profile, error: profileError } = await supabase()
          .from('profiles')
          .select('organization_id')
          .single();

        if (profileError || !profile?.organization_id) {
          if (!cancelled) {
            setSubscriptionData(null);
            setEntitlements(buildEmptyEntitlementsMap());
            setLoading(false);
          }
          return;
        }

        const orgId = profile.organization_id as string;

        // 2. Obtener suscripción activa
        const { data: sub, error: subError } = await supabase()
          .from('subscriptions')
          .select('id, plan_id, status, starts_at, ends_at, auto_renew, created_at, updated_at, canceled_at')
          .eq('organization_id', orgId)
          .in('status', ['active', 'trial', 'past_due'])
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (subError || !sub) {
          if (!cancelled) {
            // Sin suscripción activa → plan Free por defecto
            setSubscriptionData(null);
            setEntitlements(buildEmptyEntitlementsMap());
            setLoading(false);
          }
          return;
        }

        // 3. Obtener plan asociado
        const { data: plan, error: planError } = await supabase()
          .from('plans')
          .select('id, name, slug, code, price_monthly, price_yearly, currency, features')
          .eq('id', sub.plan_id as string)
          .maybeSingle();

        if (planError || !plan) {
          if (!cancelled) {
            setSubscriptionData(null);
            setEntitlements(buildEmptyEntitlementsMap());
            setLoading(false);
          }
          return;
        }

        // 4. Obtener feature_codes desde plan_features
        const { data: planFeatures, error: pfError } = await supabase()
          .from('plan_features')
          .select('feature_code')
          .eq('plan_id', plan.id as string);

        const featureCodes: string[] = pfError ? [] : (planFeatures || []).map((f) => f.feature_code as string);

        // 5. Parsear features jsonb
        const rawFeatures = (plan.features as Record<string, unknown>) || {};
        const parsedFeatures: Record<string, string | number | boolean> = {};
        for (const [key, value] of Object.entries(rawFeatures)) {
          if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            parsedFeatures[key] = value;
          }
        }

        const storageGB =
          typeof parsedFeatures.storage_gb === 'number'
            ? parsedFeatures.storage_gb
            : typeof parsedFeatures.storage_gb === 'string'
              ? parseFloat(parsedFeatures.storage_gb) || 1
              : 1;

        const subData: SubscriptionData = {
          planId: plan.id as string,
          planName: plan.name as string,
          planSlug: plan.slug as string,
          planCode: (plan.code as string) || '',
          planPrice: Number(plan.price_monthly ?? 0),
          planCurrency: (plan.currency as string) || 'USD',
          status: sub.status as string,
          featureCodes,
          storageGB,
          features: parsedFeatures,
        };

        // 6. Construir entitlements map
        const ents = buildEmptyEntitlementsMap();
        for (const code of featureCodes) {
          const key = code as EntitlementKey;
          if (ents[key]) {
            const limitVal = resolveLimitValue(parsedFeatures, key);
            ents[key] = {
              ...ents[key],
              enabled: true,
              limit: limitVal,
              limitUnit: resolveLimitUnit(key),
            };
          }
        }

        if (!cancelled) {
          setSubscriptionData(subData);
          setEntitlements(ents);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Error al cargar permisos');
          setSubscriptionData(null);
          setEntitlements(buildEmptyEntitlementsMap());
          setLoading(false);
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [refreshKey]);

  // ── NEW API ──

  const isSubscriptionActive = useMemo(() => {
    if (!subscriptionData) return false;
    return subscriptionData.status === 'active' || subscriptionData.status === 'trial';
  }, [subscriptionData]);

  const hasFeature = useCallback(
    (featureCode: string): boolean => {
      if (!subscriptionData) return false;
      if (!isSubscriptionActive) return false;
      const code = resolveFeatureCode(featureCode);
      if (!code) return false;
      return subscriptionData.featureCodes.includes(code);
    },
    [subscriptionData, isSubscriptionActive],
  );

  const getLimit = useCallback(
    (featureCode: string): number | null => {
      const code = resolveFeatureCode(featureCode);
      if (!code) return null;
      const entitlement = entitlements[code];
      return entitlement?.limit ?? null;
    },
    [entitlements],
  );

  // ── BACKWARD COMPAT ──

  const currentPlan = useMemo(() => buildPlanInfoLegacy(subscriptionData), [subscriptionData]);

  const planId = useMemo(() => currentPlan.id, [currentPlan]);

  const storageTotalGB = useMemo(() => currentPlan.storageGB, [currentPlan]);

  const storageUsedGB = useMemo(() => 0.2, []); // Demo placeholder hasta tener métrica real

  const storageUsed = useMemo(() => formatStorageUsed(storageUsedGB), [storageUsedGB]);

  const storagePercentage = useMemo(
    () => Math.min(Math.round((storageUsedGB / Math.max(storageTotalGB, 1)) * 100), 100),
    [storageUsedGB, storageTotalGB],
  );

  const isFree = useMemo(() => {
    if (!subscriptionData) return true;
    return subscriptionData.planPrice === 0;
  }, [subscriptionData]);

  const isPro = useMemo(() => {
    if (!subscriptionData) return false;
    return subscriptionData.planPrice > 0 && subscriptionData.planPrice < 80;
  }, [subscriptionData]);

  const isPremium = useMemo(() => {
    if (!subscriptionData) return false;
    return subscriptionData.planPrice >= 80;
  }, [subscriptionData]);

  const canAccess = useCallback(
    (featureId: string): boolean => {
      return hasFeature(featureId);
    },
    [hasFeature],
  );

  const getFeature = useCallback(
    (featureId: string): FeatureInfoLegacy | null => {
      const code = resolveFeatureCode(featureId);
      if (!code) return null;

      // Buscar la definición del entitlement
      const def = ENTITLEMENT_DEFINITIONS.find((d) => d.key === code);
      if (!def) return null;

      const accessible = hasFeature(featureId);
      const title = currentPlan.name;

      return {
        accessible,
        requiredPlan: {
          ...currentPlan,
          badge: accessible ? 'Desbloqueado' : currentPlan.badge,
        },
        badge: accessible ? 'Desbloqueado' : `Requiere ${title}`,
        badgeColor: currentPlan.color,
        description: def.description,
        label: def.label,
      };
    },
    [hasFeature, currentPlan],
  );

  return {
    // NEW
    loading,
    error,
    subscription: subscriptionData,
    entitlements,
    hasFeature,
    getLimit,
    isSubscriptionActive,
    refreshEntitlements,

    // BACKWARD COMPAT
    currentPlan,
    planId,
    storageUsed,
    storageTotalGB,
    storageUsedGB,
    storagePercentage,
    canAccess,
    getFeature,
    isFree,
    isPro,
    isPremium,
  };
}

// ── INTERNAL HELPERS ──

function resolveLimitValue(
  features: Record<string, string | number | boolean>,
  key: EntitlementKey,
): number | null {
  const limitKeyMap: Partial<Record<EntitlementKey, string>> = {
    extra_storage: 'storage_gb',
    multiple_stations: 'max_stations',
  };

  const mappedKey = limitKeyMap[key];
  if (mappedKey && mappedKey in features) {
    const raw = features[mappedKey];
    const num = typeof raw === 'number' ? raw : typeof raw === 'string' ? parseFloat(raw) : NaN;
    if (!Number.isNaN(num) && num > 0) return num;
  }

  return null;
}

function resolveLimitUnit(key: EntitlementKey): string | null {
  switch (key) {
    case 'extra_storage': return 'GB';
    case 'multiple_stations': return 'estaciones';
    case 'podcasts': return 'podcasts';
    case 'contests': return 'concursos';
    default: return null;
  }
}